import { useTranslation } from 'react-i18next';
import { Percent } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from '@scio3/ui';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';
import { PlanComparisonTable } from '../../components/PlanComparisonTable';

type Props = {
  selectedPlan: string;
  onPlanChange: (plan: string) => void;
  onSelectFree?: () => void;
  /** True while the Free-plan handler is in flight — disables the Free button to prevent duplicate clicks. */
  selectingFree?: boolean;
  currentPlan: string | undefined;
  /** Current currency code (e.g. 'usd', 'eur') */
  currency?: string;
  /** Called when user changes currency in the selector */
  onCurrencyChange?: (currency: string) => void;
  /** Disable currency selector (paid users with existing subscription) */
  currencyDisabled?: boolean;
  /** Minimum plan required — hides plans below this level */
  minPlanRequired?: string;
  /** Hide the Free plan bar — user is already on Free (SP45) */
  hideFreePlanOption?: boolean;
  /** Yearly / Monthly toggle — merged in from the old PAYMENT_TERM step */
  billingInterval: BillingInterval;
  onBillingIntervalChange: (v: BillingInterval) => void;
  /** When true, lock the Monthly/Yearly toggle so the user can't change it.
   * Set on Upgrade Required paywall when the customer is already on Yearly
   * (#1134 — prevent yearly→monthly downgrade through paywall). */
  lockBillingToggle?: boolean;
};

export function WizardStepPlan({
  selectedPlan,
  onPlanChange,
  onSelectFree,
  selectingFree,
  currentPlan,
  currency,
  onCurrencyChange,
  currencyDisabled,
  minPlanRequired,
  hideFreePlanOption,
  billingInterval,
  onBillingIntervalChange,
  lockBillingToggle = false,
}: Props) {
  const { t } = useTranslation();
  const isYearly =
    billingInterval === BILLING_INTERVAL.YEARLY ||
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE;

  return (
    <div className="py-2 space-y-4 mx-auto w-fit max-w-5xl">
      {/* Toggle (centered) + Currency (right) — sits where "Select Your Plan" used to live.
          Three-column grid keeps the toggle horizontally centered in the popup while the
          currency selector sits in the right column. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div />
        <div
          role="radiogroup"
          aria-label={t('Billing period')}
          aria-disabled={lockBillingToggle || undefined}
          className={cn(
            'inline-flex items-stretch rounded-lg bg-slate-200 dark:bg-panel-hover p-1 gap-1 justify-self-center',
            lockBillingToggle && 'opacity-60',
          )}
        >
          <button
            type="button"
            role="radio"
            aria-checked={!isYearly}
            disabled={lockBillingToggle}
            onClick={() => {
              if (lockBillingToggle) return;
              onBillingIntervalChange(BILLING_INTERVAL.MONTHLY);
            }}
            className={cn(
              'px-5 py-1.5 text-sm font-medium rounded-md transition-colors',
              lockBillingToggle ? 'cursor-not-allowed' : 'cursor-pointer',
              !isYearly
                ? 'bg-card text-foreground shadow-sm'
                : !lockBillingToggle && 'text-muted-foreground hover:text-foreground',
              lockBillingToggle && isYearly && 'text-muted-foreground',
            )}
          >
            {t('Billed Monthly')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isYearly}
            disabled={lockBillingToggle}
            onClick={() => {
              if (lockBillingToggle) return;
              onBillingIntervalChange(BILLING_INTERVAL.YEARLY);
            }}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-1.5 text-sm font-medium rounded-md transition-colors',
              lockBillingToggle ? 'cursor-not-allowed' : 'cursor-pointer',
              isYearly
                ? 'bg-card text-foreground shadow-sm'
                : !lockBillingToggle && 'text-muted-foreground hover:text-foreground',
              lockBillingToggle && !isYearly && 'text-muted-foreground',
            )}
          >
            <span
              className="inline-flex items-center justify-center rounded-full h-5 w-5 shrink-0 bg-primary text-white"
              aria-hidden
            >
              <Percent className="h-3 w-3" />
            </span>
            <span>
              {t('Billed Yearly')}{' '}
              <span className="font-medium text-primary">{t('(Save 10%)')}</span>
            </span>
          </button>
        </div>

        <Select value={currency} onValueChange={onCurrencyChange} disabled={currencyDisabled}>
          <SelectTrigger
            className={cn(
              'w-[120px] text-sm justify-self-end',
              currencyDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            )}
            aria-label={t('Currency')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="usd">USD $</SelectItem>
            <SelectItem value="eur">EUR &euro;</SelectItem>
            <SelectItem value="gbp">GBP &pound;</SelectItem>
            <SelectItem value="cad">CAD $</SelectItem>
            <SelectItem value="aud">AUD $</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <PlanComparisonTable
        value={selectedPlan}
        onChange={onPlanChange}
        onSelectFree={onSelectFree}
        selectingFree={selectingFree}
        currentPlan={currentPlan}
        currency={currency}
        minPlanRequired={minPlanRequired}
        hideFreePlanOption={hideFreePlanOption}
        billingInterval={billingInterval}
        onBillingIntervalChange={onBillingIntervalChange}
      />
    </div>
  );
}
